import React, { useState, useEffect, useRef } from 'react';
import { 
  Shield, Plus, Search, Edit2, Trash2, AlertTriangle, 
  CheckCircle, XCircle, Clock, Building2, Globe, User,
  Car, Phone, Mail, FileText, Calendar, Filter, Upload,
  Download, FileSpreadsheet, X
} from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = '';

const WHITELIST_TYPES = [
  { value: 'staff', label: 'Staff', color: 'bg-blue-100 text-blue-800' },
  { value: 'contractor', label: 'Contractor', color: 'bg-purple-100 text-purple-800' },
  { value: 'vip', label: 'VIP', color: 'bg-amber-100 text-amber-800' },
  { value: 'permit', label: 'Permit', color: 'bg-green-100 text-green-800' },
  { value: 'emergency', label: 'Emergency', color: 'bg-red-100 text-red-800' },
  { value: 'maintenance', label: 'Maintenance', color: 'bg-orange-100 text-orange-800' },
  { value: 'management', label: 'Management', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-800' }
];

const STATUS_STYLES = {
  active: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle },
  suspended: { bg: 'bg-amber-100', text: 'text-amber-800', icon: AlertTriangle },
  expired: { bg: 'bg-gray-100', text: 'text-gray-800', icon: Clock },
  revoked: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle }
};

// Site name to site_id mapping
const SITE_NAME_MAP = {
  'kyle rise': 'site_001',
  'test site': 'site_001',
  'retail park multi-storey': 'site_002',
  'hospital visitor car park': 'site_003',
  'waterfront retail park': 'site_004'
};

export default function AdminFreeflow() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSite, setFilterSite] = useState('');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importSiteId, setImportSiteId] = useState('');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    vehicle_registration: '',
    whitelist_type: 'staff',
    scope: 'site_only',
    site_id: '',
    holder_name: '',
    holder_email: '',
    holder_phone: '',
    department: '',
    permit_number: '',
    notes: '',
    valid_until: '',
    allow_entry: true,
    allow_exit: true
  });

  useEffect(() => {
    fetchEntries();
    fetchStats();
    fetchSites();
  }, [filterType, filterStatus, filterSite]);

  const fetchEntries = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('whitelist_type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      if (filterSite) params.append('site_id', filterSite);
      if (search) params.append('search', search);
      
      const res = await fetch(`${API_URL}/api/freeflow?${params}`, {
        credentials: 'include'
      });
      const data = await res.json();
      setEntries(data.entries || []);
    } catch (err) {
      setError('Failed to load whitelist entries');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/freeflow/stats/summary`, {
        credentials: 'include'
      });
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const fetchSites = async () => {
    try {
      // Use the dedicated freeflow sites endpoint
      const res = await fetch(`${API_URL}/api/freeflow/sites`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setSites(Array.isArray(data) ? data : []);
      } else {
        // Fallback to main sites endpoint
        const res2 = await fetch(`${API_URL}/api/sites`, {
          credentials: 'include'
        });
        if (res2.ok) {
          const data2 = await res2.json();
          setSites(Array.isArray(data2) ? data2 : []);
        }
      }
    } catch (err) {
      console.error('Failed to load sites:', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchEntries();
  };

  const openCreateModal = () => {
    setEditingEntry(null);
    setFormData({
      vehicle_registration: '',
      whitelist_type: 'staff',
      scope: 'site_only',
      site_id: sites[0]?.site_id || '',
      holder_name: '',
      holder_email: '',
      holder_phone: '',
      department: '',
      permit_number: '',
      notes: '',
      valid_until: '',
      allow_entry: true,
      allow_exit: true
    });
    setShowModal(true);
  };

  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setFormData({
      vehicle_registration: entry.vehicle_registration,
      whitelist_type: entry.whitelist_type,
      scope: entry.scope,
      site_id: entry.site_id || '',
      holder_name: entry.holder_name,
      holder_email: entry.holder_email || '',
      holder_phone: entry.holder_phone || '',
      department: entry.department || '',
      permit_number: entry.permit_number || '',
      notes: entry.notes || '',
      valid_until: entry.valid_until ? entry.valid_until.split('T')[0] : '',
      allow_entry: entry.allow_entry,
      allow_exit: entry.allow_exit
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        valid_until: formData.valid_until ? new Date(formData.valid_until).toISOString() : null
      };
      
      if (editingEntry) {
        const res = await fetch(`${API_URL}/api/freeflow/${editingEntry.whitelist_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Update failed');
      } else {
        const res = await fetch(`${API_URL}/api/freeflow`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Create failed');
        }
      }
      
      setShowModal(false);
      fetchEntries();
      fetchStats();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleStatusChange = async (newStatus, reason) => {
    try {
      const res = await fetch(`${API_URL}/api/freeflow/${statusTarget.whitelist_id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus, reason })
      });
      if (!res.ok) throw new Error('Status update failed');
      
      setShowStatusModal(false);
      setStatusTarget(null);
      fetchEntries();
      fetchStats();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API_URL}/api/freeflow/${deleteTarget.whitelist_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Delete failed');
      
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchEntries();
      fetchStats();
    } catch (err) {
      alert(err.message);
    }
  };

  // ============== IMPORT FUNCTIONS ==============
  
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      
      // Parse Kyle Rise format
      // Row 1: ANPR Whitelists (title)
      // Row 2: site name (e.g., Kyle Rise)
      // Row 3: headers (Name, Start Date, End Date, Notes, Registered Keeper)
      // Row 4+: data
      
      let siteName = '';
      let headers = [];
      let dataRows = [];
      
      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i];
        if (!row || row.length === 0) continue;
        
        // Skip title row
        if (row[0]?.toString().toLowerCase().includes('whitelist')) continue;
        
        // Detect site name row (single cell with site name)
        if (row.length === 1 || (row[0] && !row[1] && !row[2])) {
          const val = row[0]?.toString().trim();
          if (val && !val.toLowerCase().includes('name') && val.length > 2) {
            siteName = val;
            continue;
          }
        }
        
        // Detect headers row
        const firstCell = row[0]?.toString().toLowerCase() || '';
        if (firstCell.includes('name') || firstCell.includes('vrm') || firstCell.includes('registration')) {
          headers = row.map(h => h?.toString().toLowerCase().trim() || '');
          continue;
        }
        
        // Data rows
        if (headers.length > 0 && row[0]) {
          const rowData = {};
          headers.forEach((h, idx) => {
            rowData[h] = row[idx]?.toString() || '';
          });
          dataRows.push(rowData);
        }
      }
      
      // Map to import format
      const mappedRows = dataRows.map(row => {
        const vrm = row['name'] || row['vrm'] || row['registration'] || row['vehicle registration'] || '';
        const validFrom = row['start date'] || row['valid from'] || row['from'] || '';
        const validUntil = row['end date'] || row['valid until'] || row['expiry'] || row['to'] || '';
        const notes = row['notes'] || row['organisation'] || row['org'] || '';
        const holderName = row['registered keeper'] || row['keeper'] || row['holder'] || row['owner'] || '';
        
        return {
          vehicle_registration: vrm.replace(/\s/g, '').toUpperCase(),
          holder_name: holderName || 'Unknown',
          valid_from: validFrom,
          valid_until: validUntil,
          notes: notes
        };
      }).filter(r => r.vehicle_registration.length >= 2);
      
      // Resolve site_id from site name
      let resolvedSiteId = '';
      if (siteName) {
        const normalizedName = siteName.toLowerCase().trim();
        resolvedSiteId = SITE_NAME_MAP[normalizedName] || '';
        
        // Try to find in sites list
        if (!resolvedSiteId && sites.length > 0) {
          const found = sites.find(s => s.name.toLowerCase().includes(normalizedName) || normalizedName.includes(s.name.toLowerCase()));
          if (found) resolvedSiteId = found.site_id;
        }
      }
      
      setImportData({
        rows: mappedRows,
        siteName: siteName,
        detectedSiteId: resolvedSiteId
      });
      setImportSiteId(resolvedSiteId || (sites[0]?.site_id || ''));
      setShowImportModal(true);
      
    } catch (err) {
      alert('Failed to parse file: ' + err.message);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handlePreviewImport = async () => {
    if (!importData || !importSiteId) {
      alert('Please select a site');
      return;
    }
    
    setImporting(true);
    try {
      const res = await fetch(`${API_URL}/api/freeflow/import/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          rows: importData.rows,
          site_id: importSiteId
        })
      });
      
      if (!res.ok) throw new Error('Preview failed');
      
      const preview = await res.json();
      setImportPreview(preview);
    } catch (err) {
      alert('Preview failed: ' + err.message);
    } finally {
      setImporting(false);
    }
  };
  
  const handleCommitImport = async () => {
    if (!importData || !importSiteId) return;
    
    setImporting(true);
    try {
      const res = await fetch(`${API_URL}/api/freeflow/import/commit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          rows: importData.rows,
          site_id: importSiteId
        })
      });
      
      if (!res.ok) throw new Error('Import failed');
      
      const result = await res.json();
      alert(`Import complete!\n\nImported: ${result.imported}\nUpdated: ${result.updated}\nErrors: ${result.errors?.length || 0}`);
      
      setShowImportModal(false);
      setImportData(null);
      setImportPreview(null);
      fetchEntries();
      fetchStats();
    } catch (err) {
      alert('Import failed: ' + err.message);
    } finally {
      setImporting(false);
    }
  };
  
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filterSite) params.append('site_id', filterSite);
      if (filterStatus) params.append('status', filterStatus);
      
      const res = await fetch(`${API_URL}/api/freeflow/export?${params}`, {
        credentials: 'include'
      });
      
      if (!res.ok) throw new Error('Export failed');
      
      const data = await res.json();
      
      // Convert to Excel
      const ws = XLSX.utils.json_to_sheet(data.entries.map(e => ({
        'Vehicle Registration': e.vehicle_registration,
        'Holder Name': e.holder_name,
        'Type': e.whitelist_type,
        'Scope': e.scope,
        'Site': e.site_name || 'All Sites',
        'Status': e.status,
        'Valid From': e.valid_from ? new Date(e.valid_from).toLocaleDateString() : '',
        'Valid Until': e.valid_until ? new Date(e.valid_until).toLocaleDateString() : 'Indefinite',
        'Notes': e.notes || ''
      })));
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Whitelist');
      XLSX.writeFile(wb, `whitelist_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      
    } catch (err) {
      alert('Export failed: ' + err.message);
    }
  };
  
  const handleDownloadTemplate = () => {
    const template = [
      ['ANPR Whitelists'],
      ['Kyle Rise'],
      ['Name', 'Start Date', 'End Date', 'Notes', 'Registered Keeper'],
      ['AB12CDE', '01/01/2024', '31/12/2024', 'Staff parking', 'John Smith'],
      ['XY34FGH', '01/01/2024', '', 'VIP access', 'Jane Doe']
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'whitelist_template.xlsx');
  };

  const getTypeStyle = (type) => {
    return WHITELIST_TYPES.find(t => t.value === type)?.color || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-freeflow">
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept=".xlsx,.xls,.csv"
        className="hidden"
      />
      
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-7 h-7 text-blue-600" />
            Freeflow Whitelist
          </h1>
          <p className="text-gray-600 mt-1">Manage exempt vehicles for automatic entry/exit</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleDownloadTemplate}
            data-testid="download-template-btn"
            className="flex items-center gap-2 px-3 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
          >
            <Download className="w-4 h-4" />
            Template
          </button>
          <button
            onClick={handleExport}
            data-testid="export-btn"
            className="flex items-center gap-2 px-3 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            data-testid="import-btn"
            className="flex items-center gap-2 px-3 py-2 text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition"
          >
            <Upload className="w-4 h-4" />
            Import Excel/CSV
          </button>
          <button
            onClick={openCreateModal}
            data-testid="add-whitelist-btn"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="w-5 h-5" />
            Add Vehicle
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div 
            className="bg-white rounded-lg border p-4 cursor-pointer hover:border-green-400 transition-colors"
            onClick={() => setFilterStatus('active')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active</p>
                <p className="text-2xl font-bold text-green-600" data-testid="stat-active">{stats.total_active}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-200" />
            </div>
          </div>
          <div 
            className="bg-white rounded-lg border p-4 cursor-pointer hover:border-amber-400 transition-colors"
            onClick={() => setFilterStatus('suspended')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Suspended</p>
                <p className="text-2xl font-bold text-amber-600" data-testid="stat-suspended">{stats.total_suspended}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-200" />
            </div>
          </div>
          <div 
            className="bg-white rounded-lg border p-4 cursor-pointer hover:border-gray-400 transition-colors"
            onClick={() => setFilterStatus('expired')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Expired</p>
                <p className="text-2xl font-bold text-gray-600" data-testid="stat-expired">{stats.total_expired}</p>
              </div>
              <Clock className="w-8 h-8 text-gray-200" />
            </div>
          </div>
          <div 
            className="bg-white rounded-lg border p-4 cursor-pointer hover:border-blue-400 transition-colors"
            onClick={() => {
              setFilterType('');
              setFilterStatus('');
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Network-wide</p>
                <p className="text-2xl font-bold text-blue-600" data-testid="stat-network">{stats.by_scope?.network || 0}</p>
              </div>
              <Globe className="w-8 h-8 text-blue-200" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border p-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="VRM, name, or permit number..."
                data-testid="search-input"
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              data-testid="filter-type"
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              {WHITELIST_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              data-testid="filter-status"
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Active & Suspended</option>
              <option value="active">Active Only</option>
              <option value="suspended">Suspended</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site</label>
            <select
              value={filterSite}
              onChange={(e) => setFilterSite(e.target.value)}
              data-testid="filter-site"
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Sites</option>
              {sites.map(s => (
                <option key={s.site_id} value={s.site_id}>{s.name}</option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            data-testid="search-btn"
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Apply
          </button>
        </form>
      </div>

      {/* Entries Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Holder</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scope</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valid Until</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {entries.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                  No whitelist entries found
                </td>
              </tr>
            ) : (
              entries.map(entry => {
                const StatusIcon = STATUS_STYLES[entry.status]?.icon || CheckCircle;
                return (
                  <tr key={entry.whitelist_id} className="hover:bg-gray-50" data-testid={`entry-${entry.whitelist_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-gray-400" />
                        <span className="font-mono font-medium">{entry.vehicle_registration}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{entry.holder_name}</p>
                        {entry.department && (
                          <p className="text-sm text-gray-500">{entry.department}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeStyle(entry.whitelist_type)}`}>
                        {entry.whitelist_type.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm">
                        {entry.scope === 'network' ? (
                          <>
                            <Globe className="w-4 h-4 text-blue-500" />
                            <span>All Sites</span>
                          </>
                        ) : (
                          <>
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <span>{entry.site_name || 'Site'}</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {entry.valid_until ? (
                        new Date(entry.valid_until).toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'short', year: 'numeric'
                        })
                      ) : (
                        <span className="text-gray-400">Indefinite</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[entry.status]?.bg} ${STATUS_STYLES[entry.status]?.text}`}>
                        <StatusIcon className="w-3 h-3" />
                        {entry.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditModal(entry)}
                          data-testid={`edit-${entry.whitelist_id}`}
                          className="p-1 text-gray-400 hover:text-blue-600 transition"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setStatusTarget(entry); setShowStatusModal(true); }}
                          data-testid={`status-${entry.whitelist_id}`}
                          className="p-1 text-gray-400 hover:text-amber-600 transition"
                          title="Change Status"
                        >
                          <AlertTriangle className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setDeleteTarget(entry); setShowDeleteModal(true); }}
                          data-testid={`delete-${entry.whitelist_id}`}
                          className="p-1 text-gray-400 hover:text-red-600 transition"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold">
                {editingEntry ? 'Edit Whitelist Entry' : 'Add Vehicle to Whitelist'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Registration *</label>
                  <input
                    type="text"
                    value={formData.vehicle_registration}
                    onChange={(e) => setFormData({...formData, vehicle_registration: e.target.value.toUpperCase()})}
                    required
                    disabled={!!editingEntry}
                    data-testid="form-vrm"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 font-mono"
                    placeholder="AB12 CDE"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select
                    value={formData.whitelist_type}
                    onChange={(e) => setFormData({...formData, whitelist_type: e.target.value})}
                    required
                    data-testid="form-type"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {WHITELIST_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Scope *</label>
                  <select
                    value={formData.scope}
                    onChange={(e) => setFormData({...formData, scope: e.target.value})}
                    required
                    data-testid="form-scope"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="site_only">Site Only</option>
                    <option value="network">All Sites (Network)</option>
                  </select>
                </div>
                {formData.scope === 'site_only' && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Site *</label>
                    <select
                      value={formData.site_id}
                      onChange={(e) => setFormData({...formData, site_id: e.target.value})}
                      required
                      data-testid="form-site"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select site...</option>
                      {sites.map(s => (
                        <option key={s.site_id} value={s.site_id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Holder Name *</label>
                  <input
                    type="text"
                    value={formData.holder_name}
                    onChange={(e) => setFormData({...formData, holder_name: e.target.value})}
                    required
                    data-testid="form-name"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.holder_email}
                    onChange={(e) => setFormData({...formData, holder_email: e.target.value})}
                    data-testid="form-email"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={formData.holder_phone}
                    onChange={(e) => setFormData({...formData, holder_phone: e.target.value})}
                    data-testid="form-phone"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({...formData, department: e.target.value})}
                    data-testid="form-department"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Security, Facilities"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Permit Number</label>
                  <input
                    type="text"
                    value={formData.permit_number}
                    onChange={(e) => setFormData({...formData, permit_number: e.target.value})}
                    data-testid="form-permit"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., PERMIT-001"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
                  <input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                    data-testid="form-valid-until"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Leave empty for indefinite access</p>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    data-testid="form-notes"
                    rows={2}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-2 flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.allow_entry}
                      onChange={(e) => setFormData({...formData, allow_entry: e.target.checked})}
                      data-testid="form-allow-entry"
                      className="rounded"
                    />
                    <span className="text-sm">Allow Entry</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.allow_exit}
                      onChange={(e) => setFormData({...formData, allow_exit: e.target.checked})}
                      data-testid="form-allow-exit"
                      className="rounded"
                    />
                    <span className="text-sm">Allow Exit</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="form-submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  {editingEntry ? 'Save Changes' : 'Add Vehicle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Import Whitelist
              </h2>
              <button onClick={() => { setShowImportModal(false); setImportData(null); setImportPreview(null); }}>
                <X className="w-5 h-5 text-gray-500 hover:text-gray-700" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Site info */}
              {importData?.siteName && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    Detected site from file: <strong>{importData.siteName}</strong>
                  </p>
                </div>
              )}
              
              {/* Site selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Import to Site *</label>
                <select
                  value={importSiteId}
                  onChange={(e) => setImportSiteId(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="import-site-select"
                >
                  <option value="">Select site...</option>
                  {sites.map(s => (
                    <option key={s.site_id} value={s.site_id}>{s.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Row count */}
              {importData && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    Found <strong>{importData.rows.length}</strong> valid rows to import
                  </p>
                  {importData.rows.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto text-xs font-mono">
                      {importData.rows.slice(0, 5).map((r, i) => (
                        <div key={i} className="text-gray-600">
                          {r.vehicle_registration} - {r.holder_name}
                        </div>
                      ))}
                      {importData.rows.length > 5 && (
                        <div className="text-gray-400">...and {importData.rows.length - 5} more</div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Preview results */}
              {importPreview && (
                <div className="space-y-3">
                  <h3 className="font-medium text-gray-900">Import Preview</h3>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-600">{importPreview.summary.new_records}</p>
                      <p className="text-xs text-green-700">New Records</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-blue-600">{importPreview.summary.updates}</p>
                      <p className="text-xs text-blue-700">Updates</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-amber-600">{importPreview.summary.duplicates}</p>
                      <p className="text-xs text-amber-700">Duplicates</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-600">{importPreview.summary.invalid}</p>
                      <p className="text-xs text-red-700">Invalid</p>
                    </div>
                  </div>
                  
                  {importPreview.invalid_rows.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-24 overflow-y-auto">
                      <p className="text-sm font-medium text-red-800 mb-1">Invalid Rows:</p>
                      {importPreview.invalid_rows.map((r, i) => (
                        <p key={i} className="text-xs text-red-700">Row {r.row}: {r.vrm} - {r.error}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => { setShowImportModal(false); setImportData(null); setImportPreview(null); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              {!importPreview ? (
                <button
                  onClick={handlePreviewImport}
                  disabled={!importSiteId || importing}
                  data-testid="preview-import-btn"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {importing ? 'Processing...' : 'Preview Import'}
                </button>
              ) : (
                <button
                  onClick={handleCommitImport}
                  disabled={importing || (importPreview.summary.new_records === 0 && importPreview.summary.updates === 0)}
                  data-testid="commit-import-btn"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                  {importing ? 'Importing...' : `Import ${importPreview.summary.new_records + importPreview.summary.updates} Records`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && statusTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6 border-b">
              <h2 className="text-lg font-semibold">Change Status</h2>
              <p className="text-sm text-gray-500 mt-1">
                Vehicle: <span className="font-mono font-medium">{statusTarget.vehicle_registration}</span>
              </p>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Current status: <span className="font-medium">{statusTarget.status.toUpperCase()}</span>
              </p>
              <div className="grid grid-cols-2 gap-3">
                {statusTarget.status !== 'active' && (
                  <button
                    onClick={() => handleStatusChange('active', 'Reactivated by admin')}
                    data-testid="status-active-btn"
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Activate
                  </button>
                )}
                {statusTarget.status !== 'suspended' && (
                  <button
                    onClick={() => handleStatusChange('suspended', 'Suspended by admin')}
                    data-testid="status-suspend-btn"
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition"
                  >
                    <AlertTriangle className="w-5 h-5" />
                    Suspend
                  </button>
                )}
                {statusTarget.status !== 'revoked' && (
                  <button
                    onClick={() => handleStatusChange('revoked', 'Revoked by admin')}
                    data-testid="status-revoke-btn"
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-red-100 text-red-800 rounded-lg hover:bg-red-700 transition"
                  >
                    <XCircle className="w-5 h-5" />
                    Revoke
                  </button>
                )}
              </div>
            </div>
            <div className="p-6 border-t">
              <button
                onClick={() => { setShowStatusModal(false); setStatusTarget(null); }}
                className="w-full px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 text-red-600 mb-4">
                <Trash2 className="w-6 h-6" />
                <h2 className="text-lg font-semibold">Delete Whitelist Entry</h2>
              </div>
              <p className="text-gray-600">
                Are you sure you want to permanently delete the whitelist entry for{' '}
                <span className="font-mono font-medium">{deleteTarget.vehicle_registration}</span>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Holder: {deleteTarget.holder_name}
              </p>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                data-testid="confirm-delete-btn"
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
